import { Code } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NewsletterSignup from './NewsletterSignup';
import AreasServedFooter from './AreasServedFooter';
import LanguageToggle from './LanguageToggle';
import DomainChecker from './DomainChecker';
import { rememberSource } from '../utils/trackSource';

function Footer({ hideAreasServed = false }) {
  const { t } = useTranslation(['footer']);
  const { pathname } = useLocation();
  const isSpanish = pathname.startsWith('/es');

  return (
    <>
    {/* Domain Checker — promotional CTA before footer (bilingual) */}
    <section className="bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 border-t border-blue-100 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-xl mx-auto">
        <DomainChecker 
          headline={isSpanish ? "¿Necesitas un Dominio?" : "Need a Domain Name?"} 
          subtitle={isSpanish 
            ? "Todo gran negocio empieza con un gran nombre. Verifica si el tuyo está disponible — desde $15.99/año." 
            : "Every great business starts with a great name. Check if yours is available — grab it from $15.99/yr before someone else does."}
          ctaText={isSpanish ? "Obtener Este Dominio" : "Get This Domain"}
          lang={isSpanish ? "es" : "en"}
        />
      </div>
    </section>

    {/* Areas Served Section - Great for Local SEO */}
    {!hideAreasServed && <AreasServedFooter />}

    <footer className="bg-slate-900 text-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center space-x-2 mb-4 hover:opacity-80 transition-opacity">
              <Code className="h-6 w-6 text-blue-400" />
              <span className="text-xl font-bold">Web-TechServices</span>
            </Link>
            <p className="text-slate-400 text-sm">
              {t('footer:tagline')}
            </p>
            <div className="mt-4">
              <LanguageToggle variant="footer" />
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-4">{t('footer:solutions.title')}</h3>
            <ul className="space-y-2 text-slate-400 text-sm">
              <li><Link to="/" className="hover:text-white transition-colors">{t('footer:solutions.home')}</Link></li>
              <li><Link to="/dealers" className="hover:text-white transition-colors">{t('footer:solutions.dealers')}</Link></li>
              <li><Link to="/local-business" className="hover:text-white transition-colors">{t('footer:solutions.localBusinesses')}</Link></li>
              <li><Link to="/services/local-seo-domination" className="hover:text-white transition-colors">{t('footer:solutions.localSeo')}</Link></li>
              <li><Link to="/mobile-apps" className="hover:text-white transition-colors">{t('footer:solutions.mobileApps')}</Link></li>
              <li><Link to="/ai-search" className="hover:text-white transition-colors">{t('footer:solutions.aiVisibility')}</Link></li>
              <li><Link to="/blog" className="hover:text-white transition-colors">{t('footer:solutions.blog')}</Link></li>
              <li><Link to="/videos" className="hover:text-white transition-colors">Demo Videos</Link></li>
              <li><Link to="/ec-academy" className="hover:text-white transition-colors">EC Academy</Link></li>
              <li><Link to="/backlinks" className="hover:text-white transition-colors">Backlink & SEO Authority</Link></li>
              <li><Link to="/press-releases" className="hover:text-white transition-colors">{t('footer:solutions.press')}</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4">{t('footer:resources.title')}</h3>
            <ul className="space-y-2 text-slate-400 text-sm">
              <li><Link to="/#analyzer" className="hover:text-white transition-colors">{t('footer:resources.analyzer')}</Link></li>
              <li><Link to="/meta-app-review" className="hover:text-white transition-colors">{t('footer:resources.metaReview')}</Link></li>
              <li><Link to="/affiliate" onClick={rememberSource('footer')} className="hover:text-white transition-colors">{t('footer:resources.partner')}</Link></li>
              <li><Link to="/affiliate/media-kit" className="hover:text-white transition-colors">{t('footer:resources.mediaKit')}</Link></li>
              <li><Link to="/affiliate/status" className="hover:text-white transition-colors">{t('footer:resources.appStatus')}</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4">{t('footer:company.title')}</h3>
            <ul className="space-y-2 text-slate-400 text-sm">
              <li><Link to="/about" className="hover:text-white transition-colors">{t('footer:company.about')}</Link></li>
              <li><Link to="/press" className="hover:text-white transition-colors">{t('footer:company.pressMedia')}</Link></li>
              <li><Link to="/book-consultation" className="hover:text-white transition-colors">{t('footer:company.bookConsult')}</Link></li>
              <li><a href="#contact" className="hover:text-white transition-colors">{t('footer:company.contact')}</a></li>
              <li><Link to="/partner/hq" className="hover:text-white transition-colors">{t('footer:company.partnerHq')}</Link></li>
              <li><Link to="/admin/login" className="hover:text-white transition-colors">{t('footer:company.login')}</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-1">
            <NewsletterSignup variant="footer" />
          </div>
        </div>

        {/* Our Network — sister sites partners can promote */}
        <div className="border-t border-slate-800 mt-8 pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5">
            <span className="text-slate-300 text-sm font-semibold shrink-0">{isSpanish ? 'Nuestra Red:' : 'Our Network:'}</span>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <a href="https://jobdispatchpro.com" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 font-medium transition-colors" data-testid="footer-network-jdp">JobDispatchPro</a>
              <a href="https://localbrandingpro.com" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors" data-testid="footer-network-lbp">LocalBrandingPro</a>
              <a href="https://charityfundraiser.com" target="_blank" rel="noopener noreferrer" className="text-rose-400 hover:text-rose-300 font-medium transition-colors inline-flex items-center gap-1.5" data-testid="footer-network-cf">
                CharityFundraiser
                <span className="text-[10px] bg-rose-500 text-white px-1.5 py-0.5 rounded font-bold">NEW</span>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-8 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-400 text-sm">{t('footer:bottom.copyright')}</p>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-slate-300 text-xs font-semibold">{t('footer:bottom.shareTitle')}</p>
              <p className="text-slate-500 text-[10px]">{t('footer:bottom.shareSub')}</p>
            </div>
            <img
              src="/qr-share.png"
              alt={t('footer:bottom.qrAlt')}
              className="h-14 w-14 rounded bg-white p-1"
              data-testid="footer-qr-code"
            />
          </div>
        </div>
      </div>
    </footer>
    </>
  );
}

export default Footer;
